import type { ConstructionInfo, ConstructionAccountInfo, OrganizeInfoTree, JoinRequest,
  CreateCloudConstructionResponse, UpdateCloudConstructionResponse, 
  ParticipatingConstruction,
  CloudConstructionQrcodeWebInfo,
  ConstructionS3Info,ConstructionKuraemonPatrolSummary,
} from "@/types/constructionApiDef"
import type { AmnimoKuraemonLive } from "@/types/kuraemonLiveApiDef"

import { useContentsStore } from "@/stores/contents"
import { defineStore } from "pinia"
import { ref, computed, watch } from 'vue'
import { useAwsStore } from "@/stores/aws"
import { useAccountStore } from "@/stores/account"
import { useLoginStore } from "@/stores/login"
import * as loggers from "@/utils/loggers"
import { useApiStore, apiIdOf, API_ORIGIN, axiosRetryx } from "@/stores/api"
import { OK, apiDefaultDescriptions, apiMessageOf, type ApiOptions } from "@/stores/api"
import { getKnackRevision } from '@/utils/kuraemon'

export const useConstructionStore = defineStore('construction', () => {
  const contentsStore = useContentsStore()
  const accountStore = useAccountStore()
  const awsStore = useAwsStore()
  const loginStore = useLoginStore()
  const { apiCall, checkResponseError } = useApiStore()
  const sleep = (millis: number) => new Promise(resolve => setTimeout(resolve, millis));

  // 大量データ対応する対象工事
  const isMassiveDataConstruction = ref(false)

  // 工事情報
  const cloudConstructionInfo = ref<ConstructionInfo | null>(null)
 
  // クラウド工事情報取得API
  const getCloudConstruction = async (args: { 
    cloud_construction_id: string
    __apiOptions?: ApiOptions
  }) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getCloudConstruction');

    const result =  await apiCall<{
      cloud_construction_info: ConstructionInfo,
      s3_info: ConstructionS3Info
    }>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/getcloudconstruction";
      const data = {
        cloud_construction_id: args.cloud_construction_id,
        knack_revision: getKnackRevision(),
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      const apiResult = response.data;
      
      return apiResult;
    })
    if(!result) return null

    // s3 接続情報の設定（認証済み）
    await awsStore.updateS3Construction({
      cloud_construction_id: args.cloud_construction_id,
      s3_info: result.s3_info,
    })

    cloudConstructionInfo.value = initKuraemonPatrolSummary(result.cloud_construction_info)

    // アカウント名マップに更新する
    const accountList = cloudConstructionInfo.value.account_info_list.map(a=>{
      return{
        account_id: a.account_id,
        account_name: a.account_name,
      }
    })
    accountStore.updateAccountNameMap(accountList);


    //  工事でのログインアカウント情報の設定
    const  currentAccountInfo = cloudConstructionInfo.value.account_info_list.find(
      accountInfo=>accountInfo.account_id === loginStore.currentAccount?.account_id)

    if (currentAccountInfo) {
      contentsStore.dispatch.updateAccountInfo(currentAccountInfo)
    }

    return cloudConstructionInfo.value
  }


  const initKuraemonPatrolSummary = ( cloudConstruction: ConstructionInfo)=>{
    const patrolSummary: ConstructionKuraemonPatrolSummary | null = cloudConstruction.kuraemon_live_count>0 ? {
      summary_name: 'パトロール',
      client_account_kind: 'patrol',
      joined_at: 0,
      is_patrol_live: false, 
      icon_style: 'circle',
    } : null
    //todo::初期値の設定をどうするか
    return {
      ...cloudConstruction,
      kuraemon_patrol_summary: patrolSummary
    }
  }

  // クラウド工事情報取得（ポーリング）
  // 工事情報が取得済みの場合、そのまま返却する
  // 工事情報がまだ取得されていない場合、最大で10秒間ポーリング 
  // ポーリング中に取得されなかった場合、APIを発行して取得する 
  const getCloudConstructionPolling = async (constructionId: string) => {
    // 工事情報がまだ取得されていない場合、ポーリング 
    for (let index = 0; index < 10; index++) {
      if(cloudConstructionInfo.value){
        if(cloudConstructionInfo.value.cloud_construction_id === constructionId) {
          return cloudConstructionInfo.value
        } 
      }
      await sleep(1000)
    }

    // API発行
    const args = {
      cloud_construction_id: constructionId,
      __apiOptions: { noDisplayLoading: true, noErrorDialog: true }
    }
    return await getCloudConstruction(args)
  }

  // メンバー一覧を参加時刻(joined_at)降順にソートし、指定されたアカウントを先頭に設定する
  const sortedMemberList = (accountInfoList: Array<ConstructionAccountInfo>) => {
    // 参加時刻(joined_at)降順ソート
    const members = structuredClone(accountInfoList);
    members.sort((a, b) => (b.joined_at - a.joined_at))

    // アカウント情報がない場合
    if(!loginStore.currentAccount) return members
    const account_id = loginStore.currentAccount.account_id   

    // 指定されたアカウントを先頭に設定する.
    const curMember = members.filter(member => account_id === member.account_id)
    const otherMembers = members.filter(member => account_id !== member.account_id)
    return curMember.concat(otherMembers)
  }

  // 工事に参加しているメンバー
  const constructionMembers = computed(() => {
    if(!cloudConstructionInfo.value) return []
    if(!cloudConstructionInfo.value.account_info_list) return []
    return sortedMemberList(cloudConstructionInfo.value.account_info_list);
  })
  // ソートされたアカウント情報のリスト
  const getSortedAccountInfoList = (accountInfoList: Array<ConstructionAccountInfo>) => {
    return sortedMemberList(accountInfoList);
  }
  // ソートされたアカウントアイコンのリスト
  const getSortedAccountIconList = (accountInfoList: Array<ConstructionAccountInfo>, kuraemonPatrolSummary?: ConstructionKuraemonPatrolSummary|null ) => {
    const sortedAccountList = sortedMemberList(accountInfoList);
    const patrolSummary = kuraemonPatrolSummary;
    if(!patrolSummary)return sortedAccountList
    if( patrolSummary.is_patrol_live){
      // 蔵衛門パトロールアカウント情報を先頭に追加する
      return [patrolSummary, ...sortedAccountList];
    }else{
      //蔵電リリースまではつねにパトロールが先頭
      return [patrolSummary, ...sortedAccountList];
    }
  }
    // 工事に参加しているメンバー
  const constructionIcons = computed(() => {
    if(!cloudConstructionInfo.value) return []
    if(!cloudConstructionInfo.value.account_info_list) return []
    return getSortedAccountIconList(cloudConstructionInfo.value.account_info_list, cloudConstructionInfo.value.kuraemon_patrol_summary);
  })

  //  工事でのログインアカウント情報
  const accountInfo = computed(() => {
    if(!cloudConstructionInfo.value) return null
    if(!cloudConstructionInfo.value.account_info_list) return null

    const loginAccountInfo = loginStore.currentAccount
    const  accountInfo = cloudConstructionInfo.value.account_info_list.find(
      accountInfo=>accountInfo.account_id === loginAccountInfo?.account_id)
    if(!accountInfo) return null

    return accountInfo
  })


  /**
   * ConstructionKuraemonPatrolListの配列から、cloudConstructionにpatrolSummaryアカウントを追加する
   * @param patrolList ConstructionKuraemonPatrolList[]
   */
  const addPatrolToCloudConstruction = (
    patrolList: Array<{ construction_id: string; kuraemon_live_list: Array<AmnimoKuraemonLive> }>,
    makeConstructionPatrolSummary: (kuraemonPatrolList: Array<AmnimoKuraemonLive>) => ConstructionKuraemonPatrolSummary| null
  ) => {
    if(!cloudConstructionInfo.value) return null
    for (const patrol of patrolList) {
      if (cloudConstructionInfo.value.cloud_construction_id !== patrol.construction_id) continue;

      if (patrol.kuraemon_live_list.length==0) {
        cloudConstructionInfo.value.kuraemon_patrol_summary=null;
      }
      cloudConstructionInfo.value.kuraemon_patrol_summary = makeConstructionPatrolSummary(patrol.kuraemon_live_list);
    }
  };

  // QRコード
  type QrInfo = {
    qrcode_data: string 
    qrcode_data_url: string
  }
  const cloudConstructionQrCode = ref<QrInfo | null>(null)

  // QRコード作成
  const createCloudConstructionQrcode = async (
    args: { 
      cloud_construction_id: string, 
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('createCloudConstructionQrcode');

    return await apiCall<QrInfo>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/createcloudconstructionqrcode";
      const data = {
        cloud_construction_id: args.cloud_construction_id,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      const apiResult = {
        qrcode_data_url: response.data.qrcode_data_url,
        qrcode_data: response.data.qrcode_data,
      }
      
      cloudConstructionQrCode.value = apiResult
      return apiResult;
    })
  }

  // クラウド工事メンバー追加API
  const addMemberToCloudConstruction = async ( 
    args: {
      cloud_construction_id: string,
      account_id: string,
      mail?: string,
      embedding_param?: any
      role_type?: string
      __apiOptions?: ApiOptions
    } 
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('addMemberToCloudConstruction');

    return await apiCall<"OK" | "MEMBER_EXISTS" | "FREEMAIL_DOMAIN">(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/addmembertocloudconstruction";
      const data = {
        cloud_construction_id: args.cloud_construction_id,
        mail: args.mail,
        embedding_param: args.embedding_param,
        account_id: args.account_id,
        role_type: args.role_type,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
        401001: apiMessageOf(401001),
        401004: apiMessageOf(401004),
      }

      const MEMBER_EXISTS_CODE = 201;
      const FREEMAIL_DOMAIN_CODE = 407;
      if (checkResponseError(response, descriptions, apiOptions, [OK, MEMBER_EXISTS_CODE, FREEMAIL_DOMAIN_CODE])) {
        return; // エラー
      }
      if(response.data.code === MEMBER_EXISTS_CODE){
        return "MEMBER_EXISTS";
      }
      if(response.data.code === FREEMAIL_DOMAIN_CODE){
        return "FREEMAIL_DOMAIN";
      }
      return 'OK';
    })

  }

  // クラウド工事メンバー複数追加API
  const bulkAddMemberMailToCloudConstruction = async (
    args: { 
      cloud_construction_id: string, 
      mail_list: string[], 
      embedding_param: {
        cloud_construction_id: string,
        cloud_construction_name: string,
        user_name: string,
        user_message: string
        qrcode_data: string
      }, 
      role_type: string,
      invite_route?: string,
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('bulkAddMemberMailToCloudConstruction');

    return await apiCall<'OK' | { error: 'INVITED_OR_INVALID_MAIL_ERROR' ,account_info_list: Array<{ mail: string }>}>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/bulkaddmembermailtocloudconstruction";
      const data = {
        cloud_construction_id: args.cloud_construction_id,
        mail_list: args.mail_list,
        embedding_param: args.embedding_param,
        role_type: args.role_type,
        invite_route: args.invite_route,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      const INVITED_OR_INVALID_MAIL_ERROR = 200002
      const okCodes = [OK, INVITED_OR_INVALID_MAIL_ERROR]
      if (checkResponseError(response, descriptions, apiOptions, okCodes)) {
        return;
      }
      const account_info_list = response.data.account_info_list
      if(response.data.code === INVITED_OR_INVALID_MAIL_ERROR
        && account_info_list && 0 < account_info_list.length){
        // すでに招待済み、または、利用不可のメールアドレスが指定された
        return {
          error: 'INVITED_OR_INVALID_MAIL_ERROR',
          account_info_list,
        }
      }
      return 'OK'
    })


  }

  // クラウド工事メンバー削除API 
  const deleteMemberToCloudConstruction = async (
    args: { 
      cloud_construction_id: string, 
      delete_account_id: string,
      is_last_manager_deletion_confirmed: boolean,
      embedding_param: {
        cloud_construction_id: string,
        cloud_construction_name: string,
      }, 
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('deleteMemberToCloudConstruction');

    return await apiCall<'OK' | "PIX_ACCOUNT_LEFT_NG" | "CAM_ACCOUNT_LEFT_NG" | "LAST_MANAGER_DELETING_NG" | "BOOKRACK_EDITOR_LEFT_NG">(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/deletemembertocloudconstruction";
      const data = {
        cloud_construction_id: args.cloud_construction_id,
        delete_account_id: args.delete_account_id,
        embedding_param: args.embedding_param,
        is_last_manager_deletion_confirmed: args.is_last_manager_deletion_confirmed,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const PIX_ACCOUNT_LEFT_NG_CODE = 408;
      const CAM_ACCOUNT_LEFT_NG_CODE = 400025;
      const LAST_MANAGER_DELETING_NG_CODE = 409;
      const BOOKRACK_EDITOR_LEFT_NG_CODE = 401026;
      const descriptions = {
        ...apiDefaultDescriptions,
        401002: `メンバーを削除する権限がありません
        工事の管理者に権限を付与するようご連絡ください`,
      }

      if (checkResponseError(response, descriptions, apiOptions, [OK, PIX_ACCOUNT_LEFT_NG_CODE, CAM_ACCOUNT_LEFT_NG_CODE, LAST_MANAGER_DELETING_NG_CODE, BOOKRACK_EDITOR_LEFT_NG_CODE])) {
        return;
      }
      if(response.data.code === PIX_ACCOUNT_LEFT_NG_CODE){
        return "PIX_ACCOUNT_LEFT_NG";
      }
      if(response.data.code === CAM_ACCOUNT_LEFT_NG_CODE){
        return "CAM_ACCOUNT_LEFT_NG";
      }
      if(response.data.code === LAST_MANAGER_DELETING_NG_CODE){
        return "LAST_MANAGER_DELETING_NG";
      }
      if(response.data.code === BOOKRACK_EDITOR_LEFT_NG_CODE){
        return "BOOKRACK_EDITOR_LEFT_NG";
      }
      if(loginStore.currentAccount?.account_id === args.delete_account_id){
        // ログインユーザーを工事から削除する場合、指定工事のデータを削除する
        contentsStore.dispatch.deleteConstruction(args.cloud_construction_id)
      }
      return 'OK'
    })
  }

  // 工事メンバー権限変更API
  const changeMemberPermissions = async ( 
    args: { 
      cloud_construction_id: string;
      account_id: string;
      role_type: string;
      __apiOptions?: ApiOptions
    } 
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('changeMemberPermissions');

    return await apiCall<"OK" | "LAST_MANAGER_ERR" | "BOOKRACK_EDITOR_ERR">(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/changememberpermissions";
      const data = {
        cloud_construction_id: args.cloud_construction_id,
        account_id: args.account_id,
        role_type: args.role_type,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const LAST_MANAGER_ERR_CODE = 408;
      const BOOKRACK_EDITOR_ERR_CODE = 401026;
      const descriptions = {
        ...apiDefaultDescriptions,
      }

      if(checkResponseError(response, descriptions, apiOptions, [OK, LAST_MANAGER_ERR_CODE, BOOKRACK_EDITOR_ERR_CODE])){
        return;
      }
      if(response.data.code === LAST_MANAGER_ERR_CODE){
        return "LAST_MANAGER_ERR";
      }
      if(response.data.code === BOOKRACK_EDITOR_ERR_CODE){
        return "BOOKRACK_EDITOR_ERR"
      }
      return 'OK'
    })

  }

  // 工事の管理者かどうかを返す
  const isCurrentConstructionManager = computed(() => {
    if(!accountInfo.value) return false;

    // 管理者かどうかを返す
    return accountInfo.value.role_type === 'manager'
  })

  /****************
   * 工事作成関連  *
   ****************/
  // 新規作成する工事名称
  const newConstructionName = ref<string>("")
    // 要領区分
  const guidelineCategory = ref<number | null>(null)
  // 要領 knackType
  const guideline = ref(null)
  // 要領 knackId
  const subGuideline = ref(null)
  // 大分類
  const broadCategory = ref(null)
  // クラウド台帳共有使用フラグ ※デフォルト値：TRUE
  const isUsableCloudAlbumShare = ref(true)

  const initNewConstructionParams = () => {
    newConstructionName.value = ''
    guidelineCategory.value = null
    guideline.value = null
    subGuideline.value = null
    broadCategory.value = null
    isUsableCloudAlbumShare.value = true
  }

  // クラウド工事作成API
  const createCloudConstruction = async(args: { 
    cloud_construction_name: string,
    cloud_construction_knack_id: number,
    organize_info_tree: OrganizeInfoTree | undefined,
    is_usable_cloud_album_share: boolean,
    __apiOptions?: ApiOptions
   }): Promise< undefined | CreateCloudConstructionResponse > =>{ 
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('createCloudConstruction');

    return await apiCall<CreateCloudConstructionResponse>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/createcloudconstruction";
      const data: any = {
        cloud_construction_name: args.cloud_construction_name,
        cloud_construction_knack_id: args.cloud_construction_knack_id,
        is_usable_cloud_album_share: args.is_usable_cloud_album_share,
      }
      if(args.organize_info_tree){
        data.organize_info_tree = args.organize_info_tree
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      return response.data.cloud_construction_info;
    })
  }

  // クラウド工事情報更新API
  type ConstructionSettings = {
    cloud_construction_start_ts?: string
    cloud_construction_end_ts?: string
    builder_name?: string
  } 
  type KokubanSettings = {
    is_default_builder_disp?: boolean
    is_default_shooting_date_disp?: boolean
  } 
  const updateCloudConstruction = async(args: { 
    cloud_construction_id: string
    update_items?: any
    construction_settings?: ConstructionSettings
    kokuban_settings?: KokubanSettings
    is_restricted_member_join?: boolean
    management_cloud_construction_name?: string
    __apiOptions?: ApiOptions
   }) => { 
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('updateCloudConstruction');

    return await apiCall<UpdateCloudConstructionResponse>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/updatecloudconstruction";
      const data = {
        cloud_construction_id: args.cloud_construction_id,
        update_items: args.update_items,
        construction_settings: args.construction_settings,
        kokuban_settings: args.kokuban_settings,
        is_restricted_member_join: args.is_restricted_member_join,
        management_cloud_construction_name: args.management_cloud_construction_name,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      return response.data.update_cloud_construction_info;
    })
  }


  // 参加申請リスト
  const joinRequestList = ref<Array<JoinRequest> | null>(null)

  // クラウド工事参加申請受付確認API
  const receiveRequestJoinCloudConstruction = async (args: { 
    __apiOptions?: ApiOptions
  }) => { 
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('receiveRequestJoinCloudConstruction');

    return await apiCall<number>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/receiverequestjoincloudconstruction";
      const data = {
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }

      joinRequestList.value = response.data.join_request_list
      return response.data.polling_milisec
    })
  }
  // クラウド工事参加承認API
  const confirmJoinCloudConstruction = async (args: { 
    cloud_construction_id: string,
    account_id: string,
    is_accept: boolean,
    role_type?: string,
    __apiOptions?: ApiOptions
  }) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('confirmJoinCloudConstruction');

    return await apiCall<'OK' | "ALREADY_PROCESSED">(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/confirmjoincloudconstruction";
      const data = {
        cloud_construction_id: args.cloud_construction_id,
        account_id: args.account_id,
        is_accept: args.is_accept,
        role_type: args.role_type,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const ALREADY_PROCESSED_CODE = 201;
      const descriptions = {
        ...apiDefaultDescriptions,
        401001: apiMessageOf(401001),
        401004: apiMessageOf(401004),
      }

      if (checkResponseError(response, descriptions, apiOptions, [OK, ALREADY_PROCESSED_CODE])) {
        return;
      }
      if(response.data.code === ALREADY_PROCESSED_CODE){
        return "ALREADY_PROCESSED";
      }
      return 'OK';
    })
  }
  const cloudConstructionId = computed(() => cloudConstructionInfo.value?.cloud_construction_id)
  watch (
    () => cloudConstructionId.value,
    (newCloudConstructionId) => {
      if (newCloudConstructionId) {
        loggers.setConstructionId(newCloudConstructionId)
        loggers.info(`update constructionId: ${newCloudConstructionId}`);
      }
    }
  )

  const participatingConstruction = ref<ParticipatingConstruction | null>(null)

  const clearParticipatingConstruction = async () => {
    participatingConstruction.value = null
  }


  // 招待工事トークン解決API
  const canJoinInvitedCloudConstructionMember = async (args: { 
    onetime_token: string,
    __apiOptions?: ApiOptions
  }) =>{ 
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('canJoinInvitedCloudConstructionMember');

    return await apiCall<'WRONG_USER_ERROR' | ParticipatingConstruction | { cloud_construction_id: string, invite_route: 'bookrack' }>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/canjoininvitedcloudconstructionmember";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
        401: `工事招待メールに記載されているリンクの有効期限が切れています
              工事に参加しているメンバーに再度招待を依頼し、
              送られてきた新しいメールのリンクより工事に参加してください`
      }

      // 工事参加が制限されている戻り値コード
      const forbiddenCode = 201;
      // 招待されていないユーザーが開いた時のエラー
      const wrongUserErrorCode = 401005;
      if (checkResponseError(response, descriptions, apiOptions, [OK, forbiddenCode, wrongUserErrorCode])) {
        return;
      }
      if(forbiddenCode === response.data.code){
        const apiResult = {
          invited_cloud_construction_name: response.data.invited_cloud_construction_name,
          joining_cloud_construction_name: response.data.joining_cloud_construction_name,
        }
        participatingConstruction.value = apiResult;
        return apiResult;
      }
      if(wrongUserErrorCode === response.data.code){
        return "WRONG_USER_ERROR";
      }
      const apiResult = {
        cloud_construction_id: response.data.cloud_construction_id,
        invite_route: response.data.invite_route
      }
      return apiResult;
    })
  }

  // 招待工事だけにするAPI
  const joinCloudConstructionMemberOnlyInvite = async (args: { 
    onetime_token: string,
    __apiOptions?: ApiOptions
  }) => { 
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('joinCloudConstructionMemberOnlyInvite');

    return await apiCall<'WRONG_USER_ERROR' | { cloud_construction_id: string }>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/joincloudconstructionmemberonlyinvite";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      
      // 招待されていないユーザーが開いた時のエラー
      const wrongUserErrorCode = 401005;
      if (checkResponseError(response, descriptions, apiOptions, [OK, wrongUserErrorCode])) {
        return;
      }
      if(wrongUserErrorCode === response.data.code){
        return "WRONG_USER_ERROR";
      }
      return {
        cloud_construction_id: response.data.cloud_construction_id
      }
    })
  }


  // クラウド工事QRコードWEB情報取得API
  const getCloudConstructionQrcodeWebInfo = async (args: { 
    onetime_token: string,
    __apiOptions?: ApiOptions
  }): Promise< undefined | 'EXPIRED_TOKEN' |CloudConstructionQrcodeWebInfo> =>{ 
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getCloudConstructionQrcodeWebInfo');

    return await apiCall<'EXPIRED_TOKEN' |CloudConstructionQrcodeWebInfo>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/getcloudconstructionqrcodewebinfo";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      const apiResult = {
        qrcode_data_url: response.data.qrcode_data_url,
        cloud_construction_id: response.data.cloud_construction_id,
        cloud_construction_name: response.data.cloud_construction_name,
      }
      return apiResult;
    })
  }

  return {
    isMassiveDataConstruction,
    cloudConstructionInfo,
    constructionMembers,
    constructionIcons,
    accountInfo,
    isCurrentConstructionManager,
    getCloudConstruction,
    getSortedAccountInfoList,
    getSortedAccountIconList,
    getCloudConstructionPolling,
    addPatrolToCloudConstruction,

    newConstructionName,
    guidelineCategory,
    guideline,
    subGuideline,
    broadCategory,
    isUsableCloudAlbumShare,
    initNewConstructionParams,
    createCloudConstruction,
    
    updateCloudConstruction,

    joinRequestList,
    receiveRequestJoinCloudConstruction,
    confirmJoinCloudConstruction,
    addMemberToCloudConstruction,
    bulkAddMemberMailToCloudConstruction,
    deleteMemberToCloudConstruction,
    createCloudConstructionQrcode,
    cloudConstructionQrCode,
    changeMemberPermissions,

    participatingConstruction,
    clearParticipatingConstruction,

    canJoinInvitedCloudConstructionMember,
    joinCloudConstructionMemberOnlyInvite,

    getCloudConstructionQrcodeWebInfo,
  }
})